/** NID information correction application (Form-2, rule 4) — data helpers. */

/**
 * The nine subjects item ৩ can correct, in the order the printed table lists
 * them. Each one owns three cells: what the card says now, what it should say,
 * and the paperwork that proves it.
 */
export const CORRECTION_ROWS = [
    { key: 'nameBn', letter: 'ক' },
    { key: 'nameEn', letter: 'খ' },
    { key: 'fatherName', letter: 'গ' },
    { key: 'motherName', letter: 'ঘ' },
    { key: 'spouseName', letter: 'ঙ' },
    { key: 'birthDate', letter: 'চ' },
    { key: 'address', letter: 'ছ' },
    { key: 'bloodGroup', letter: 'জ' },
    { key: 'other', letter: 'ঝ' },
];

/** The three cells of one table row, as keys on the flat form object. */
export const cellKeys = (rowKey) => ({
    current: `${rowKey}Current`,
    corrected: `${rowKey}Corrected`,
    docs: `${rowKey}Docs`,
});

const TABLE_FIELDS = CORRECTION_ROWS.reduce((acc, row) => {
    const keys = cellKeys(row.key);
    acc[keys.current] = '';
    acc[keys.corrected] = '';
    acc[keys.docs] = '';
    return acc;
}, {});

export const INITIAL_NID_CORRECTION_FORM = {
    // ১ — the national ID card holder
    holderName: '',
    holderNid: '',
    // ২ — legal guardian, for a holder under 18 or declared incapable by court
    guardianName: '',
    guardianNid: '',
    // ৩ — the correction table
    ...TABLE_FIELDS,
    // ৪-৬
    feeAmount: '',
    feeReceipt: '',
    attachedDocs: '',
    // signature block — applicant (right column of the printed form)
    applicantSignName: '',
    applicantSignAddress: '',
    applicantSignMobile: '',
    applicantSignEmail: '',
    // signature block — legal guardian (left column, only where applicable)
    guardianSignName: '',
    guardianSignAddress: '',
    guardianSignMobile: '',
    guardianSignEmail: '',
};

export function displayField(value) {
    const v = String(value ?? '').trim();
    return v || '';
}

/** Convert Bengali/Arabic-Indic digits to ASCII — used only for validation logic. */
function toAsciiDigits(str) {
    return str
        .replace(/[০-৯]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0x09e6 + 48))
        .replace(/[٠-٩]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0x0660 + 48));
}

const BENGALI_DIGITS = '০১২৩৪৫৬৭৮৯';

/** Form-2 is printed in Bengali, so its numbers are written in Bengali too. */
export function toBengaliDigits(value) {
    return String(value ?? '').replace(/\d/g, (digit) => BENGALI_DIGITS[Number(digit)]);
}

/**
 * A date as the form writes it: দিন-মাস-বছর in Bengali numerals, the shape the
 * printed line and the field's own placeholder use.
 * @param {Date | null} date
 */
export function formatDateField(date) {
    if (!date || Number.isNaN(date.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return toBengaliDigits(`${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`);
}

/**
 * Read a stored date back for the picker. Anything the field could already
 * hold is accepted — Bengali or ASCII digits, and -, / or . as the separator.
 * @returns {Date | null}
 */
export function parseDateField(value) {
    const match = toAsciiDigits(String(value ?? '').trim())
        .match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
    if (!match) return null;
    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const date = new Date(year, month - 1, day);
    // Reject a date the calendar rolled over, e.g. 31-02-2020.
    if (date.getDate() !== day || date.getMonth() !== month - 1) return null;
    return date;
}

/** Keep only digit characters (ASCII 0-9, Bengali ০-৯, Arabic-Indic ٠-٩). */
export function normalizeDigits(value, maxLength) {
    const digits = String(value ?? '').replace(/[^\d০-৯٠-٩]/g, '');
    return maxLength ? digits.slice(0, maxLength) : digits;
}

/** Strip to digits, drop leading 880 country code if present. */
export function normalizeBdPhone(value) {
    let digits = String(value ?? '').replace(/[^\d০-৯٠-٩]/g, '');
    const ascii = toAsciiDigits(digits);
    if (ascii.startsWith('880') && ascii.length >= 13) {
        digits = digits.slice(3);
    }
    return digits.slice(0, 11);
}

/** Every Bangladeshi mobile number is 11 digits starting with 0. */
export function isValidBdPhone(value) {
    const ascii = toAsciiDigits(normalizeBdPhone(value));
    return /^0\d{10}$/.test(ascii);
}

/** The form only asks for an e-mail "যদি থাকে", so this runs on non-empty input. */
export function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value ?? '').trim());
}

const FORM_SNAPSHOT_KEY = 'nid-correction-form-last-snapshot';

/**
 * What the office cannot process without. The guardian block stays optional —
 * item ২ only applies to a holder under 18 or declared incapable by a court —
 * and so does every individual table cell, because which rows get filled is the
 * whole point of the application.
 */
const REQUIRED_FIELDS = [
    'holderName',
    'holderNid',
    'applicantSignName',
    'applicantSignAddress',
    'applicantSignMobile',
];

/** @returns {typeof INITIAL_NID_CORRECTION_FORM | null} */
export function getNidCorrectionFormSnapshot() {
    if (typeof window === 'undefined') return null;
    try {
        const raw = localStorage.getItem(FORM_SNAPSHOT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return { ...INITIAL_NID_CORRECTION_FORM, ...parsed };
    } catch {
        return null;
    }
}

/** Save the latest application so it can be restored later. */
export function saveNidCorrectionFormSnapshot(form) {
    if (typeof window === 'undefined' || !form) return;
    try {
        const payload = {};
        for (const key of Object.keys(INITIAL_NID_CORRECTION_FORM)) {
            payload[key] = displayField(form[key]);
        }
        localStorage.setItem(FORM_SNAPSHOT_KEY, JSON.stringify(payload));
    } catch {
        /* ignore */
    }
}

export function getMobileValidationError(value, t) {
    const digits = normalizeBdPhone(value);
    if (!digits) return '';
    if (digits.length < 11) {
        return t('nidCorrectionForm.validation.mobileLength', { count: digits.length });
    }
    if (!isValidBdPhone(digits)) {
        return t('nidCorrectionForm.validation.mobileInvalid');
    }
    return '';
}

export function getEmailValidationError(value, t) {
    const email = displayField(value);
    if (!email) return '';
    return isValidEmail(email) ? '' : t('nidCorrectionForm.validation.emailInvalid');
}

/**
 * A row is only usable once the office can see both sides of the change. One
 * side on its own is the mistake this catches: "correct my father's name" with
 * nothing to correct it *to* gets the whole application rejected under
 * instruction (১৬).
 *
 * @param {typeof INITIAL_NID_CORRECTION_FORM} form
 * @returns {{ key: string, hasCurrent: boolean, hasCorrected: boolean }[]}
 */
export function getCorrectionRowStates(form) {
    return CORRECTION_ROWS.map((row) => {
        const keys = cellKeys(row.key);
        return {
            key: row.key,
            hasCurrent: Boolean(displayField(form?.[keys.current])),
            hasCorrected: Boolean(displayField(form?.[keys.corrected])),
        };
    });
}

/** How many rows of item ৩ are filled in on both sides. */
export function countCompleteCorrections(form) {
    return getCorrectionRowStates(form).filter((r) => r.hasCurrent && r.hasCorrected).length;
}

/**
 * @param {typeof INITIAL_NID_CORRECTION_FORM} form
 * @param {(key: string, opts?: object) => string} t
 * @returns {{ field: string, message: string }[]}
 */
export function getNidCorrectionFormValidationErrors(form, t) {
    const errors = [];

    for (const key of REQUIRED_FIELDS) {
        if (key === 'applicantSignMobile') continue;
        if (!displayField(form[key])) {
            errors.push({
                field: key,
                message: t('nidCorrectionForm.validation.required', {
                    field: t(`nidCorrectionForm.fields.${key}`),
                }),
            });
        }
    }

    const mobile = displayField(form.applicantSignMobile);
    if (!mobile) {
        errors.push({ field: 'applicantSignMobile', message: t('nidCorrectionForm.validation.mobileRequired') });
    } else {
        const mobileErr = getMobileValidationError(mobile, t);
        if (mobileErr) errors.push({ field: 'applicantSignMobile', message: mobileErr });
    }

    const guardianMobileErr = getMobileValidationError(form.guardianSignMobile, t);
    if (guardianMobileErr) {
        errors.push({ field: 'guardianSignMobile', message: guardianMobileErr });
    }

    for (const key of ['applicantSignEmail', 'guardianSignEmail']) {
        const emailErr = getEmailValidationError(form[key], t);
        if (emailErr) errors.push({ field: key, message: emailErr });
    }

    // Item ৩ is the application: without one complete row there is nothing to
    // correct, and a row filled on one side only cannot be acted on either.
    const states = getCorrectionRowStates(form);
    if (!states.some((r) => r.hasCurrent && r.hasCorrected)) {
        errors.push({
            field: 'corrections',
            message: t('nidCorrectionForm.validation.noCorrection'),
        });
    }
    for (const row of states) {
        if (row.hasCurrent && !row.hasCorrected) {
            errors.push({
                field: cellKeys(row.key).corrected,
                message: t('nidCorrectionForm.validation.correctedMissing', {
                    field: t(`nidCorrectionForm.rows.${row.key}`),
                }),
            });
        } else if (!row.hasCurrent && row.hasCorrected) {
            errors.push({
                field: cellKeys(row.key).current,
                message: t('nidCorrectionForm.validation.currentMissing', {
                    field: t(`nidCorrectionForm.rows.${row.key}`),
                }),
            });
        }
    }

    return errors;
}

export function isNidCorrectionFormComplete(form, t) {
    return getNidCorrectionFormValidationErrors(form, t).length === 0;
}

/**
 * What the download confirmation lists back to the applicant.
 *
 * Unlike Form-13, this form is *meant* to leave most of its blanks empty: an
 * application that corrects one name legitimately leaves eight table rows and
 * the whole guardian block blank, so naming all of them would be noise. Only
 * the blanks that would actually stall the application are listed — the fields
 * the office needs, and any half-filled table row.
 *
 * @param {typeof INITIAL_NID_CORRECTION_FORM} form
 * @param {(key: string, opts?: object) => string} t
 * @returns {string[]}
 */
export function getEmptyNidCorrectionFields(form, t) {
    const missing = [];

    for (const key of REQUIRED_FIELDS) {
        if (!displayField(form?.[key])) missing.push(t(`nidCorrectionForm.fields.${key}`));
    }

    const states = getCorrectionRowStates(form);
    if (!states.some((r) => r.hasCurrent && r.hasCorrected)) {
        missing.push(t('nidCorrectionForm.fields.corrections'));
    }
    for (const row of states) {
        const name = t(`nidCorrectionForm.rows.${row.key}`);
        if (row.hasCurrent && !row.hasCorrected) {
            missing.push(t('nidCorrectionForm.fields.rowCorrected', { field: name }));
        } else if (!row.hasCurrent && row.hasCorrected) {
            missing.push(t('nidCorrectionForm.fields.rowCurrent', { field: name }));
        }
    }

    return missing;
}

/** Everything the preview prints, already trimmed. */
export function buildNidCorrectionLetter(form) {
    const out = {};
    for (const key of Object.keys(INITIAL_NID_CORRECTION_FORM)) {
        out[key] = displayField(form?.[key]);
    }
    return out;
}

/** @param {{ field: string, message: string }[]} errors */
export function validationErrorsToFieldMap(errors) {
    const map = {};
    for (const { field, message } of errors) {
        map[field] = message;
    }
    return map;
}
